import IMAGESIZES from "src/constants/IMAGESIZES";
import Post from "src/constants/Post";
import { BuildServerlessBookCoverImageUrl, formatImageUrl } from "helpers/Helper";

export const isEbbHomepage = (postOrType) => {
  const postType =
    typeof postOrType === "object" && postOrType !== null ? postOrType.post_type : postOrType;
  return Number(postType) === Post.TYPES.EBB_HOMEPAGE;
};

export const getHomepagePost = (props) =>
  props?.data?.[0]?.Post?.post || props?.data?.[0]?.Index?.post || {};

export const getPostSlug = (post) => {
  const slug = post?.slug;
  if (Array.isArray(slug)) return slug.filter(Boolean).join("/");
  return String(slug || "").replace(/^\/+|\/+$/g, "");
};

const plainTextFromOrion = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/\s+/g, " ")
    .trim();
};

const mediaName = (media) => media?.name || media?.Name || "";

const promoTypeValue = (promoType) =>
  typeof promoType === "string" ? promoType : promoType?.promoType || "";

const ribbonClass = (component) => String(component?.props?.classes || "").trim().toLowerCase();

const isBookRibbon = (component) =>
  promoTypeValue(component?.props?.promoType) === "book" ||
  ribbonClass(component) === "book-deals" ||
  ribbonClass(component) === "book deals";

export const extractHomepageSignup = (post) => {
  const newsletter = (post?.components || []).find((component) => component?.type === "newsletter-v2");
  const props = newsletter?.props || {};
  const ribbonModal = (post?.components || []).find((component) => {
    const options = component?.props?.newsletterOptions?.props;
    return (
      component?.type === "promo-ribbon" &&
      component?.props?.button &&
      options?.subComponentType === "modal" &&
      !isBookRibbon(component)
    );
  });
  const dealsRibbonModal = (post?.components || []).find((component) => {
    const options = component?.props?.newsletterOptions?.props;
    return (
      component?.type === "promo-ribbon" &&
      isBookRibbon(component) &&
      component?.props?.button &&
      options?.subComponentType === "modal"
    );
  });
  const ribbonNewsletter = ribbonModal?.props?.newsletterOptions?.props;
  const dealsNewsletter = dealsRibbonModal?.props?.newsletterOptions?.props;
  const pageIsModal = props.subComponentType === "modal";
  const modalNewsletter = ribbonNewsletter || (pageIsModal ? props : null);

  return {
    header: plainTextFromOrion(props.header || props.modalHeader),
    modalHeader: plainTextFromOrion(
      (modalNewsletter && (modalNewsletter.modalHeader || modalNewsletter.header)) ||
        props.modalHeader ||
        props.header
    ),
    buttonText: plainTextFromOrion(
      props.buttonText ||
        ribbonNewsletter?.newsletterSignUpButtonText ||
        ribbonNewsletter?.buttonText
    ),
    signUpLocation: (props.signUpLocation || "").trim(),
    pageNewsletter: props,
    isModal: Boolean(modalNewsletter),
    newsletterProps: modalNewsletter || {},
    dealsNewsletter: dealsNewsletter || null,
    dealsModalHeader: plainTextFromOrion(dealsNewsletter?.modalHeader || dealsNewsletter?.header),
  };
};

const normalizeImageUrl = (value) => {
  if (!value || typeof value !== "string") return value || "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (/^\/?[\w.-]+\.[a-z]{2,}\//i.test(trimmed)) return `https://${trimmed.replace(/^\/+/, "")}`;
  return trimmed;
};

const sizedUrl = (name, size) => {
  if (!name) return "";
  const normalized = normalizeImageUrl(name);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  try {
    return formatImageUrl(name, size);
  } catch {
    return "";
  }
};

const promoImageUrls = (image) => {
  if (!image) return { desktop: "", mobile: "" };
  if (typeof image === "string") {
    const normalized = normalizeImageUrl(image);
    if (/^https?:\/\//i.test(normalized)) {
      const mdMatch = normalized.match(/\/md_(.+)$/);
      if (mdMatch) {
            return {
          desktop: sizedUrl(mdMatch[1], "lg") || normalized,
          mobile: sizedUrl(mdMatch[1], "lg") || normalized,
        };
      }
      return { desktop: normalized, mobile: normalized };
    }
    return { desktop: sizedUrl(image, "lg"), mobile: sizedUrl(image, "lg") };
  }
  const name = mediaName(image);
  return { desktop: sizedUrl(name, "lg"), mobile: sizedUrl(name, "lg") };
};

const DEFAULT_HERO_DESKTOP = "Background-Image-Large.png";
const DEFAULT_HERO_MOBILE = "Banner-Mobile.png";

const s3Image = (fileName) => {
  const name = String(fileName || "").trim();
  if (!name) return "";
  if (/^https?:\/\//i.test(name)) return name;
  const base = String(process.env.S3_IMAGE_URL || "").replace(/\/+$/, "");
  return `${base}/${name.replace(/^\/+/, "")}`;
};

export const extractHomepageMedia = ({ heroDesktopFile, heroMobileFile } = {}) => ({
  heroDesktop: s3Image(heroDesktopFile || DEFAULT_HERO_DESKTOP),
  heroMobile: s3Image(heroMobileFile || DEFAULT_HERO_MOBILE),
});

const cardImage = (card) => {
  if (typeof card?.image === "string") return normalizeImageUrl(card.image);
  return mediaName(card?.image);
};

export const extractHomepageFeatures = (post) => {
  const instructional = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && ribbonClass(component) === "instructional"
  );
  return (instructional?.props?.promoCards || [])
    .filter((card) => card?.image || card?.text)
    .map((card) => ({
      heading: plainTextFromOrion(card.text),
      body: plainTextFromOrion(card.description || card.subtext),
      imageUrl: cardImage(card),
    }));
};

export const extractHomepageClosing = (post) => {
  const ribbons = (post?.components || []).filter((component) => component?.type === "promo-ribbon");
  const closingRibbon =
    ribbons.find((component) => ribbonClass(component) === "retailer") ||
    ribbons.find((component) => !isBookRibbon(component) && ribbonClass(component) !== "instructional");

  if (!closingRibbon) {
    return { header: "", imageDesktop: "", imageMobile: "" };
  }

  const firstCard = (closingRibbon.props?.promoCards || []).find((card) => card?.image);
  const urls = promoImageUrls(firstCard?.image);
  return {
    header: plainTextFromOrion(closingRibbon.props?.header),
    imageDesktop: urls.desktop,
    imageMobile: urls.mobile,
  };
};

const isbnFromImageUrl = (url = "") => {
  const match = String(url).match(/(\d{10,13})/);
  return match ? match[1] : "";
};

const readCoverUrl = (affiliate = {}) => {
  const { image, isbn, primary_isbn } = affiliate;
  const imageName =
    image?.props?.image?.Name ||
    image?.props?.image?.name ||
    (typeof image === "string" ? image : "");

  if (imageName) {
    if (/^https?:\/\//i.test(imageName)) return imageName;
    return BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_SEARCHRESULT, imageName);
  }

  const isbnValue = isbn || primary_isbn || "";
  if (isbnValue) {
    return BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_SEARCHRESULT, `${isbnValue}.jpg`);
  }

  return "";
};

export const extractHomepageTestimonials = (post) => {
  const slider = (post?.components || []).find(
    (component) => component?.type === "quote-slider" || component?.type === "inline-quotes"
  );

  return (slider?.props?.quotes || [])
    .map((quote) => ({
      text: plainTextFromOrion(quote?.text),
      author: plainTextFromOrion(quote?.author),
    }))
    .filter((quote) => quote.text);
};

export const extractHomepageDeals = (post) => {
  const dealsRibbon = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && isBookRibbon(component)
  );

  if (!dealsRibbon) {
    return { header: "", subheader: "", books: [] };
  }

  const books = (dealsRibbon.props?.promoCards || [])
    .filter((card) => card?.image)
    .map((card) => {
      const imageUrl =
        typeof card.image === "string" ? card.image : readCoverUrl({ image: card.image });
      return {
        title: plainTextFromOrion(card.text),
        isbn: isbnFromImageUrl(imageUrl),
        price: card.salePrice || "",
        imageUrl,
        link: card.link || "",
      };
    })
    .filter((book) => book.imageUrl);

  return {
    header: plainTextFromOrion(dealsRibbon.props?.header),
    subheader: plainTextFromOrion(dealsRibbon.props?.subheader),
    books,
  };
};

export const formatBadgePrice = (price) => {
  if (price === null || price === undefined || price === "") return "";
  const cleaned = String(price).trim().replace(/^\$/, "");
  const num = Number(cleaned);
  if (Number.isNaN(num)) return cleaned.replace(/\$/g, "");
  if (num === 0) return "FREE";
  return num.toFixed(2).replace(/\.00$/, "");
};
